import React from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { Button } from '../Button';

export type ArTabId = 'balances' | 'follow-ups' | 'links';

export interface CollectionsShellProps {
  active: ArTabId;
  eyebrow: string;
  onSelectTab?: ((tab: ArTabId) => void) | undefined;
  onOpenSettings?: (() => void) | undefined;
  onCreateLink?: (() => void) | undefined;
  onNewFollowUp?: (() => void) | undefined;
  /** On Payment links, Create link is the primary action and New follow-up is gone. */
  createIsPrimary?: boolean | undefined;
  children: React.ReactNode;
}

const TABS: { id: ArTabId; label: string }[] = [
  { id: 'balances', label: 'Balances' },
  { id: 'follow-ups', label: 'Follow-ups' },
  { id: 'links', label: 'Payment links' },
];

/**
 * The Collections header + three tabs (amendment 28 §2), shared by Balances,
 * Follow-ups and Payment links. One title, one eyebrow slot, one row of actions.
 */
export function CollectionsShell({
  active,
  eyebrow,
  onSelectTab,
  onOpenSettings,
  onCreateLink,
  onNewFollowUp,
  createIsPrimary,
  children,
}: CollectionsShellProps) {
  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[1200px] flex-1 overflow-y-auto px-[30px] pb-16 pt-5">
      {/* header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="pc-eyebrow flex items-center gap-1.5 text-[var(--text-secondary)]">
            <Icon kind="credit" size={12} stroke={2} style={{ color: 'var(--brand-indigo)' }} />
            <span>{eyebrow}</span>
          </div>
          <h1 className="m-0 mt-0.5 font-[var(--font-display)] text-[24px] font-semibold tracking-[-0.02em] text-[var(--text-primary)]">
            Collec<span className="text-[var(--brand-indigo)]">tions</span>
          </h1>
          <p className="m-0 mt-0.5 text-[12.5px] text-[var(--text-secondary)]">
            Balances patients owe — who to follow up, and the payment links that collect them.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" iconLeft="cog" onClick={onOpenSettings}>Settings</Button>
          <Button variant={createIsPrimary === true ? 'primary' : 'ghost'} size="sm" iconLeft="plus" onClick={onCreateLink}>Create link</Button>
          {createIsPrimary === true ? null : (
            <Button variant="primary" size="sm" iconLeft="plus" onClick={onNewFollowUp}>New follow-up</Button>
          )}
        </div>
      </div>

      {/* tabs */}
      <div className="mt-4 flex items-center gap-1 border-b border-[var(--border-default)]">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => { onSelectTab?.(t.id); }}
            className={cn(
              'relative -mb-px px-3.5 py-2 text-[13px] font-semibold',
              active === t.id
                ? 'border-b-2 border-[var(--brand-indigo)] text-[var(--text-primary)]'
                : 'border-b-2 border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)]',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {children}
    </div>
  );
}
